'use es6';

import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { List as ImmutableList, Map as ImmutableMap } from 'immutable';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import SenderAvatar from '../sender-avatar/SenderAvatar';
import { LAST_IN_GROUP, FIRST_IN_GROUP, LAST } from 'conversations-message-history/constants/messagePositionDescriptors';
import { AGENT, VISITOR } from 'conversations-message-history/constants/messageUses';
import PositionDescriptors from 'conversations-message-history/proptypes/PositionDescriptors';
import SenderMap from 'conversations-message-history/proptypes/SenderMap';
import SenderHeaderContent from '../headers/SenderHeaderContent';
import RecordPropType from 'conversations-prop-types/prop-types/RecordPropType';
import { isSenderLoading } from 'conversations-message-history/senders/operators/isSenderLoading';
import { getOptionValue, getOptionLabel } from 'conversations-message-history/quick-reply/operators/quickReplyGetters';
import VizExButton from 'visitor-ui-component-library/button/VizExButton';
import { getQuickReply, getQuickReplyReplies, getQuickReplyAllowMultiSelect } from 'conversations-message-history/common-message-format/operators/cmfQuickReplyGetters';
import { getCitations } from 'conversations-message-history/common-message-format/operators/cmfCitationGetters';
import PrimaryMessage from '../primary-message/components/PrimaryMessage';
import PrimaryMessageAvatar from '../primary-message/components/PrimaryMessageAvatar';
import PrimaryMessageFooter from '../primary-message/components/PrimaryMessageFooter';
import { getExistingFeedback } from '../message-feedback/operators/getExistingFeedback';
import SourcesAndFeedback from '../message-feedback/SourcesAndFeedback';
import { shouldShowFeedback } from '../message-feedback/operators/shouldShowFeedback';
import PrimaryMessageHeader from '../primary-message/components/PrimaryMessageHeader';
import PrimaryMessageHeaderContent from '../primary-message/components/PrimaryMessageHeaderContent';
import QuickReplyGroup from '../quick-reply/QuickReplyGroup';
import { getHasQuickReplyOptions } from '../quick-reply/operators/getHasQuickReplyOptions';
import { MAX_QUICK_REPLY_BUTTONS } from '../quick-reply/constants/quickReplies';
import QuickReplySelect from '../quick-reply/QuickReplySelect';
import QuickLinksGroup from '../quick-links/QuickLinksGroup';
import { getQuickLinks, getHasQuickLinks } from 'conversations-message-history/common-message-format/operators/cmfQuickLinksGetters';
import CommonMessageContent from './CommonMessageContent';
import VizExSmall from 'visitor-ui-component-library/typography/VizExSmall';
import { getTimestamp } from 'conversations-message-history/common-message-format/operators/commonMessageFormatGetters';
import { getSenderFromMessageSenders } from './operators/getSenderFromMessageSenders';
import { isFromVisitor } from 'conversations-message-history/common-message-format/operators/senderTypeComparators';
import { getFirstName } from 'conversations-message-history/senders/operators/getters';
import SourcesLine from '../ai-sources/SourcesLine';
import { getMessageType } from 'conversations-message-history/configured-message-type/operators/configuredMessageTypeGetters';
import { PARTIAL_MESSAGE } from 'conversations-message-history/partial-message/constants/messageTypes';
import GeneratingIndicator from '../atoms/GeneratingIndicator';
import PlayMessageButton from './PlayMessageButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
class CommonMessage extends PureComponent {
  constructor(props) {
    super(props);
    this.handleQuickReplySend = () => {
      const {
        message,
        publishMessage
      } = this.props;
      const {
        selectedQuickReply
      } = this.state;
      const text = getOptionLabel(selectedQuickReply);
      const quickReply = getQuickReply(message);
      const updatedQuickReply = quickReply.set('quickReplies', [selectedQuickReply]);
      publishMessage({
        text,
        quickReply: updatedQuickReply
      });
    };
    this.handleQuickReplySelect = ({
      target: {
        value
      }
    }) => {
      const {
        message
      } = this.props;
      const options = getQuickReplyReplies(message);
      const selected = options.filter(option => getOptionValue(option) === value).first();
      this.setState({
        selectedQuickReply: selected
      });
    };
    this.showQuickReplies = () => {
      const {
        hasUnansweredQuickReplyMessage,
        message,
        positionDescriptors
      } = this.props;
      const hasNotBeenAnswered = hasUnansweredQuickReplyMessage && positionDescriptors.get(LAST);
      return getHasQuickReplyOptions(message) && hasNotBeenAnswered;
    };
    this.showQuickLinks = () => {
      const {
        message
      } = this.props;
      return getHasQuickLinks(message);
    };
    this.showQuickReplySelection = () => {
      const {
        message
      } = this.props;
      const quickReplies = getQuickReplyReplies(message);
      const allowMultiSelect = getQuickReplyAllowMultiSelect(message);
      return this.showQuickReplies() && quickReplies.count() > MAX_QUICK_REPLY_BUTTONS && !allowMultiSelect;
    };
    this.renderQuickReplyGroup = () => {
      const {
        accentColor,
        isThreadStarted,
        message,
        publishMessage,
        requiresExplicitConsentToProcess
      } = this.props;
      const quickReplies = getQuickReplyReplies(message);
      const allowMultiSelect = getQuickReplyAllowMultiSelect(message);
      const shouldRenderCitations = getCitations(message).size > 0;
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [shouldRenderCitations && this.renderSources(), /*#__PURE__*/_jsx(PrimaryMessageFooter, {
          children: /*#__PURE__*/_jsx(QuickReplyGroup, {
            options: quickReplies,
            disabled: requiresExplicitConsentToProcess || !isThreadStarted,
            message: message,
            multiSelect: allowMultiSelect,
            publishMessage: publishMessage,
            onChange: this.handleQuickReplyUpdate,
            accentColor: accentColor
          })
        })]
      });
    };
    this.renderQuickLinksGroup = () => {
      const {
        accentColor,
        onQuickLinkClick,
        message
      } = this.props;
      const quickLinks = getQuickLinks(message);
      const shouldRenderCitations = getCitations(message).size > 0;
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [shouldRenderCitations && this.renderSources(), /*#__PURE__*/_jsx(PrimaryMessageFooter, {
          children: /*#__PURE__*/_jsx(QuickLinksGroup, {
            options: quickLinks,
            message: message,
            accentColor: accentColor,
            onQuickLinkClick: onQuickLinkClick
          })
        })]
      });
    };
    this.getEffectiveFeedback = () => {
      const {
        message,
        unpublishedMessages,
        threadHistoryData
      } = this.props;
      return getExistingFeedback(message.id, unpublishedMessages, threadHistoryData);
    };
    this.shouldShowFeedback = () => {
      const {
        isUngatedForCustomerAgentFeedback,
        isThreadClosed,
        isThreadHistoryLoaded,
        message
      } = this.props;
      return shouldShowFeedback({
        isUngatedForCustomerAgentFeedback,
        isThreadClosed,
        isThreadHistoryLoaded,
        message
      });
    };
    this.renderSourcesContent = () => {
      const {
        browserWindowHeight,
        message,
        navigateToKnowledgeBaseArticle,
        trackInteraction
      } = this.props;
      const citations = getCitations(message);
      if (!citations.length) {
        return null;
      }
      return /*#__PURE__*/_jsx(SourcesLine, {
        browserWindowHeight: browserWindowHeight,
        navigateToKnowledgeBaseArticle: navigateToKnowledgeBaseArticle,
        sources: citations.toJS(),
        trackInteraction: trackInteraction
      });
    };
    this.renderSources = () => {
      const content = this.renderSourcesContent();
      return content ? /*#__PURE__*/_jsx(PrimaryMessageFooter, {
        children: content
      }) : null;
    };
    this.renderSourcesAndFeedback = (showFeedback, selectedFeedback, hasCitations) => {
      return /*#__PURE__*/_jsx(SourcesAndFeedback, {
        sourcesContent: hasCitations ? this.renderSourcesContent() : null,
        showFeedback: showFeedback,
        selectedFeedback: selectedFeedback,
        onFeedbackClick: this.handleFeedbackClick
      });
    };
    this.renderQuickReplySendButton = () => {
      const {
        isThreadStarted
      } = this.props;
      const {
        selectedQuickReply
      } = this.state;
      return /*#__PURE__*/_jsx(PrimaryMessageFooter, {
        children: /*#__PURE__*/_jsx(VizExButton, {
          disabled: !isThreadStarted || !getOptionValue(selectedQuickReply),
          onClick: this.handleQuickReplySend,
          size: "sm",
          use: "primary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "conversations-visitor-message-history.quickReply.selectionComplete"
          })
        })
      });
    };
    this.renderQuickReplySelect = () => {
      const {
        message,
        isThreadStarted,
        requiresExplicitConsentToProcess,
        requiresExplicitCookieConsent
      } = this.props;
      const {
        selectedQuickReply
      } = this.state;
      const quickReplies = getQuickReplyReplies(message);
      return /*#__PURE__*/_jsx("div", {
        className: "m-y-2",
        children: /*#__PURE__*/_jsx(QuickReplySelect, {
          quickReplyOptions: quickReplies,
          disabled: requiresExplicitConsentToProcess || requiresExplicitCookieConsent || !isThreadStarted,
          onChange: this.handleQuickReplySelect,
          value: getOptionValue(selectedQuickReply),
          placeholder: I18n.text('conversations-visitor-message-history.quickReply.selectionPlaceholder')
        })
      });
    };
    this.handleFeedbackClick = feedbackType => {
      const {
        dispatchFeedbackChain,
        message
      } = this.props;
      if (!dispatchFeedbackChain) {
        return Promise.resolve();
      }
      return dispatchFeedbackChain({
        messageId: message.id,
        feedbackType
      });
    };
    this.state = {
      selectedQuickReply: ImmutableMap()
    };
  }
  isSentFromVisitor() {
    return isFromVisitor(this.props.message);
  }
  get sender() {
    const {
      message,
      senders
    } = this.props;
    return getSenderFromMessageSenders(message, senders);
  }
  renderMessageHeader() {
    const {
      positionDescriptors
    } = this.props;
    const isFirstInGroup = positionDescriptors.includes(FIRST_IN_GROUP);
    if (!isFirstInGroup || this.isSentFromVisitor()) {
      return null;
    }
    const sender = this.sender;
    const isLoading = isSenderLoading(sender);
    return /*#__PURE__*/_jsxs(PrimaryMessageHeader, {
      children: [/*#__PURE__*/_jsx(PrimaryMessageAvatar, {
        children: /*#__PURE__*/_jsx(SenderAvatar, {
          sender: sender,
          isLoading: isLoading
        })
      }), /*#__PURE__*/_jsx(PrimaryMessageHeaderContent, {
        children: /*#__PURE__*/_jsx(SenderHeaderContent, {
          isSenderLoading: isLoading,
          sender: sender
        })
      })]
    });
  }
  renderTimeStamp() {
    const {
      message,
      positionDescriptors,
      locale
    } = this.props;
    const isLastInGroupMessage = positionDescriptors.includes(LAST_IN_GROUP);
    const timestamp = getTimestamp(message);
    if (!isLastInGroupMessage || !timestamp) {
      return null;
    }
    return /*#__PURE__*/_jsx(PrimaryMessageFooter, {
      children: /*#__PURE__*/_jsx(VizExSmall, {
        use: "help",
        children: new Date(timestamp).toLocaleTimeString(locale, {
          hour: '2-digit',
          minute: '2-digit'
        })
      })
    });
  }
  renderGeneratingMessage() {
    return /*#__PURE__*/_jsx(PrimaryMessageFooter, {
      children: /*#__PURE__*/_jsx(GeneratingIndicator, {})
    });
  }
  renderMessageFooter() {
    const {
      message
    } = this.props;
    const quickReplies = getQuickReplyReplies(message);
    const allowMultiSelect = getQuickReplyAllowMultiSelect(message);
    if (this.showQuickReplies() && this.showQuickReplySelection()) {
      return this.renderQuickReplySendButton();
    } else if (this.showQuickReplies() && (quickReplies.count() <= MAX_QUICK_REPLY_BUTTONS || allowMultiSelect)) {
      return this.renderQuickReplyGroup();
    } else if (this.showQuickLinks()) {
      return this.renderQuickLinksGroup();
    } else {
      const showFeedback = this.shouldShowFeedback();
      const hasCitations = getCitations(message).size > 0;
      if (hasCitations || showFeedback) {
        const {
          sentiment
        } = showFeedback ? this.getEffectiveFeedback() : {
          sentiment: null
        };
        return this.renderSourcesAndFeedback(showFeedback, sentiment, hasCitations);
      } else if (getMessageType(message) === PARTIAL_MESSAGE) {
        return this.renderGeneratingMessage();
      }
      return null;
    }
  }
  renderPlayMessageButton() {
    const {
      message,
      positionDescriptors,
      speechPocActive = false,
      audioPlaybackStatus = 'paused',
      messagesList
    } = this.props;
    return /*#__PURE__*/_jsx(PlayMessageButton, {
      message: message,
      positionDescriptors: positionDescriptors,
      speechPocActive: speechPocActive,
      audioPlaybackStatus: audioPlaybackStatus,
      messagesList: messagesList
    });
  }
  render() {
    const {
      fileAttachments,
      isRejected,
      message,
      positionDescriptors,
      toggleOpen,
      mobile,
      hasUnansweredQuickReplyMessage,
      locale,
      navigateToKnowledgeBaseArticle,
      browserWindowHeight
    } = this.props;
    const use = this.isSentFromVisitor() ? VISITOR : AGENT;
    const timestamp = new Date(getTimestamp(message)).toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit'
    });
    return /*#__PURE__*/_jsxs(PrimaryMessage, {
      use: use,
      children: [this.renderMessageHeader(), /*#__PURE__*/_jsx(CommonMessageContent, {
        senderName: getFirstName(this.sender),
        fileAttachments: fileAttachments,
        isLastMessage: positionDescriptors.includes(LAST),
        isRejected: isRejected,
        message: message,
        timestamp: timestamp,
        quickReplySelect: this.showQuickReplySelection() && this.renderQuickReplySelect,
        mobile: mobile,
        toggleOpen: toggleOpen,
        hasUnansweredQuickReplyMessage: hasUnansweredQuickReplyMessage,
        speechPocActive: this.props.speechPocActive,
        playMessageButton: this.renderPlayMessageButton(),
        navigateToKnowledgeBaseArticle: navigateToKnowledgeBaseArticle,
        browserWindowHeight: browserWindowHeight,
        messagesList: this.props.messagesList
      }), this.renderMessageFooter()]
    });
  }
}
CommonMessage.displayName = 'CommonMessage';
CommonMessage.propTypes = {
  accentColor: PropTypes.string,
  audioPlaybackStatus: PropTypes.string,
  browserWindowHeight: PropTypes.number,
  dispatchFeedbackChain: PropTypes.func,
  fileAttachments: PropTypes.instanceOf(ImmutableList),
  gates: PropTypes.object.isRequired,
  hasUnansweredQuickReplyMessage: PropTypes.bool,
  isRejected: PropTypes.bool,
  isThreadClosed: PropTypes.bool,
  isThreadHistoryLoaded: PropTypes.bool,
  isThreadStarted: PropTypes.bool.isRequired,
  isUngatedForCustomerAgentFeedback: PropTypes.bool,
  locale: PropTypes.string.isRequired,
  message: PropTypes.oneOfType([RecordPropType('CommonMessage'), RecordPropType('PartialMessage')]).isRequired,
  messagesList: PropTypes.instanceOf(ImmutableList),
  mobile: PropTypes.bool,
  navigateToKnowledgeBaseArticle: PropTypes.func,
  onQuickLinkClick: PropTypes.func,
  positionDescriptors: PositionDescriptors.isRequired,
  publishMessage: PropTypes.func,
  requiresExplicitConsentToProcess: PropTypes.bool.isRequired,
  requiresExplicitCookieConsent: PropTypes.bool.isRequired,
  senders: SenderMap,
  speechPocActive: PropTypes.bool,
  threadHistoryData: PropTypes.object,
  toggleOpen: PropTypes.func,
  trackInteraction: PropTypes.func,
  unpublishedMessages: PropTypes.object
};
export default CommonMessage;